import { useState } from "react";
import { useSiteContent } from "../context/SiteContent";
import { submitContact } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { useToast } from "../hooks/use-toast";
import SocialLinksRow from "../components/SocialLinksRow";
import SeoHead from "../components/SeoHead";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const Contact = () => {
  const { personalInfo, socialLinks } = useSiteContent();
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast({ title: "Please fill in all required fields", variant: "destructive" });
      return;
    }
    setIsSubmitting(true);
    try {
      await submitContact(form);
      toast({ title: "Message sent successfully", description: "Thank you for reaching out. I will respond shortly." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast({ title: "Failed to send message", variant: "destructive" });
    }
    setIsSubmitting(false);
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "ContactPage",
    name: `Contact ${personalInfo.name}`,
    mainEntity: {
      "@type": "Person",
      name: personalInfo.name,
      email: personalInfo.contact.emailWork || personalInfo.contact.email,
      telephone: personalInfo.contact.phone || personalInfo.contact.phoneUK,
      address: { "@type": "PostalAddress", addressLocality: personalInfo.contact.address },
    },
  };

  return (
    <div>
      <SeoHead
        title={`Contact ${personalInfo.name || 'Abhilash Puljal'} | ${personalInfo.title || 'Strategy Advisor'}`}
        description={`Get in touch with ${personalInfo.name || 'Abhilash Puljal'} for strategy consulting and international development partnerships.`}
        path="/contact"
        jsonLd={jsonLd}
      />

      <section className="py-20 bg-[#FAF8F4]">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <p className="text-xs tracking-[0.3em] uppercase text-[#B8945A] font-sans mb-4">Reach Out</p>
          <h1 className="font-serif text-4xl md:text-5xl font-bold text-[#1C2B3A] mb-4">Get in Touch</h1>
          <div className="w-20 h-[2px] bg-[#B8945A]"></div>
          <p className="font-sans text-[#7A7A7A] mt-6 max-w-xl leading-relaxed">If you are working on cross-border strategy across India, Africa or Asia — or thinking about what those corridors mean for your next move — this is the right place to start.</p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-14">
            <div className="lg:col-span-3">
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2"><Label htmlFor="name" className="font-sans text-sm text-[#1C2B3A]">Name *</Label><Input id="name" name="name" value={form.name} onChange={handleChange} placeholder="Your full name" data-testid="contact-name-input" className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans" /></div>
                  <div className="space-y-2"><Label htmlFor="email" className="font-sans text-sm text-[#1C2B3A]">Email *</Label><Input id="email" name="email" type="email" value={form.email} onChange={handleChange} placeholder="your@email.com" data-testid="contact-email-input" className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans" /></div>
                </div>
                <div className="space-y-2"><Label htmlFor="subject" className="font-sans text-sm text-[#1C2B3A]">Subject</Label><Input id="subject" name="subject" value={form.subject} onChange={handleChange} placeholder="Subject" data-testid="contact-subject-input" className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans" /></div>
                <div className="space-y-2"><Label htmlFor="message" className="font-sans text-sm text-[#1C2B3A]">Message *</Label><Textarea id="message" name="message" value={form.message} onChange={handleChange} placeholder="Your message..." rows={6} data-testid="contact-message-input" className="border-[#E5DDD3] focus-visible:ring-[#B8945A]/20 font-sans resize-none" /></div>
                <Button type="submit" disabled={isSubmitting} data-testid="contact-submit-btn" className="bg-[#1C2B3A] hover:bg-[#2A3F54] text-white px-8 py-2.5 rounded font-sans text-sm tracking-wider inline-flex items-center gap-2 h-auto">{isSubmitting ? "Sending..." : "Send Message"} <Send size={14} /></Button>
              </form>
            </div>
            <div className="lg:col-span-2">
              <div className="bg-[#FAF8F4] rounded-lg p-8 border border-[#E5DDD3] sticky top-24" data-testid="contact-info">
                <h3 className="font-serif text-lg font-bold text-[#1C2B3A] mb-6">Contact Information</h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4"><Mail size={18} className="text-[#B8945A] flex-shrink-0 mt-0.5" /><div><p className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-1">Email</p>{personalInfo.contact.email && <a href={`mailto:${personalInfo.contact.email}`} className="font-sans text-sm text-[#1C2B3A] hover:text-[#B8945A] transition-colors block">{personalInfo.contact.email}</a>}{personalInfo.contact.emailWork && <a href={`mailto:${personalInfo.contact.emailWork}`} className="font-sans text-sm text-[#1C2B3A] hover:text-[#B8945A] transition-colors block mt-0.5">{personalInfo.contact.emailWork}</a>}{personalInfo.contact.emailAcademic && <a href={`mailto:${personalInfo.contact.emailAcademic}`} className="font-sans text-sm text-[#1C2B3A] hover:text-[#B8945A] transition-colors block mt-0.5">{personalInfo.contact.emailAcademic}</a>}</div></div>
                  {(personalInfo.contact.phone || personalInfo.contact.phoneUK) && <div className="flex items-start gap-4"><Phone size={18} className="text-[#B8945A] flex-shrink-0 mt-0.5" /><div><p className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-1">Phone</p>{personalInfo.contact.phone && <p className="font-sans text-sm text-[#1C2B3A]">{personalInfo.contact.phone}</p>}{personalInfo.contact.phoneUK && <p className="font-sans text-sm text-[#1C2B3A] mt-0.5">{personalInfo.contact.phoneUK}</p>}</div></div>}
                  {personalInfo.contact.address && <div className="flex items-start gap-4"><MapPin size={18} className="text-[#B8945A] flex-shrink-0 mt-0.5" /><div><p className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-1">Location</p><p className="font-sans text-sm text-[#1C2B3A]">{personalInfo.contact.address}</p></div></div>}
                  {(personalInfo.social.linkedin || (socialLinks && socialLinks.length > 0)) && (
                    <div className="pt-4 border-t border-[#E5DDD3]">
                      <p className="font-sans text-xs uppercase tracking-wider text-[#7A7A7A] mb-3">Connect</p>
                      <SocialLinksRow links={socialLinks} legacyLinkedin={personalInfo.social.linkedin} testid="contact-social-links" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
